import { useReducedMotion } from 'motion/react'
import { useEffect, useRef } from 'react'

export interface LottieAnimationData {
  default: object
}

const LottieAnimation = ({
  loadAnimationData,
}: {
  loadAnimationData: () => Promise<LottieAnimationData>
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const prefersReducedMotion = useReducedMotion()

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    let isCancelled = false
    let destroyAnimation: (() => void) | undefined

    Promise.all([
      import('lottie-web/build/player/lottie_light'),
      loadAnimationData(),
    ])
      .then(([{ default: lottie }, { default: animationData }]) => {
        if (isCancelled) {
          return
        }

        const animation = lottie.loadAnimation({
          animationData,
          autoplay: !prefersReducedMotion,
          container,
          loop: false,
          renderer: 'svg',
        })

        if (prefersReducedMotion) {
          animation.goToAndStop(animation.totalFrames - 1, true)
        }

        destroyAnimation = () => animation.destroy()
      })
      .catch(() => {
        // The animation is purely decorative, so if its chunk fails to load
        // (e.g. a stale bundle after a deploy) we leave the space empty.
      })

    return () => {
      isCancelled = true
      destroyAnimation?.()
    }
  }, [loadAnimationData, prefersReducedMotion])

  return <div ref={containerRef} aria-hidden="true" className="h-full w-full" />
}

export default LottieAnimation
